import pMemoize from "p-memoize";
import fetch from "cross-fetch";
async function checkSource(src) {
  var _a;
  try {
    if ((_a = await fetch(src, { method: "HEAD" })) == null ? void 0 : _a.ok) {
      return src;
    }
  } catch {
    return void 0;
  }
  return void 0;
}
async function getSources({ sources }) {
  let src = null;
  const validSources = sources == null ? void 0 : sources.filter(Boolean);
  const blob = validSources == null ? void 0 : validSources.find((source) => source.startsWith("blob:"));
  const file = validSources == null ? void 0 : validSources.find((source) => !source.startsWith("http") && !source.startsWith("blob:"));
  if (blob || file) {
    return blob ?? file;
  }
  if (validSources.length > 0) {
    const sPromises = validSources.map((src2) => checkSource(src2));
    src = (await Promise.all(sPromises)).find(Boolean);
  }
  return src;
}
async function readContent(url) {
  try {
    const response = await fetch(url);
    return response.text();
  } catch {
    return null;
  }
}
async function getContent(content) {
  content = content.trim();
  const isUrl = content.startsWith("http") || content.startsWith("blob:");
  return isUrl ? readContent(content) : (
    // If it's not a url assume it's the string content already
    content
  );
}
const getCaptions = pMemoize(
  async ({ original, translation, originalLanguage = "", translationLanguage = "" }, { language }) => {
    var _a, _b;
    const languageMap2 = {
      [(_a = translationLanguage == null ? void 0 : translationLanguage.split("-")) == null ? void 0 : _a[0]]: translation,
      [(_b = originalLanguage == null ? void 0 : originalLanguage.split("-")) == null ? void 0 : _b[0]]: original
    };
    const resolvedCaptions = (language && languageMap2[language]) ?? original;
    if (!resolvedCaptions) {
      return null;
    }
    return getContent(resolvedCaptions);
  },
  {
    cacheKey: ([args, { language }]) => JSON.stringify({ ...args, language })
  }
);
async function getVideos(data) {
  var _a, _b, _c, _d, _e, _f;
  const video = (_b = (_a = data == null ? void 0 : data.answer) == null ? void 0 : _a.media) == null ? void 0 : _b.video;
  const input = (_d = (_c = data == null ? void 0 : data.answer) == null ? void 0 : _c.media) == null ? void 0 : _d.input;
  const playlist = (_f = (_e = data == null ? void 0 : data.answer) == null ? void 0 : _e.media) == null ? void 0 : _f.playlist;
  return {
    src: await getSources({ sources: [video, input] }),
    playlist: await getSources({ sources: [playlist] })
  };
}
function getNames(data) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  const name = ((_b = (_a = data == null ? void 0 : data.answer) == null ? void 0 : _a.contact) == null ? void 0 : _b.name) === "Anonymous" ? "" : ((_e = (_d = (_c = data == null ? void 0 : data.answer) == null ? void 0 : _c.contact) == null ? void 0 : _d.name) == null ? void 0 : _e.trim()) ?? "";
  const companyName = ((_h = (_g = (_f = data == null ? void 0 : data.answer) == null ? void 0 : _f.contact) == null ? void 0 : _g.client) == null ? void 0 : _h.name) === "Not Supplied" ? "" : ((_l = (_k = (_j = (_i = data == null ? void 0 : data.answer) == null ? void 0 : _i.contact) == null ? void 0 : _j.client) == null ? void 0 : _k.name) == null ? void 0 : _l.trim()) ?? "";
  const [firstName, lastName] = name.split(" ");
  return {
    name,
    firstName,
    lastName,
    companyName
  };
}
async function deriveData({ rootData, index }, options) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
  return {
    ...getNames(rootData),
    ...await getVideos(rootData),
    key: `${rootData == null ? void 0 : rootData.id}-${index}`,
    captions: await getCaptions(
      {
        original: ((_b = (_a = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _a.media) == null ? void 0 : _b.captions) ?? ((_d = (_c = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _c.captions) == null ? void 0 : _d.current),
        translation: ((_f = (_e = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _e.media) == null ? void 0 : _f.translation) ?? ((_h = (_g = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _g.captions) == null ? void 0 : _h.translation),
        originalLanguage: (_j = (_i = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _i.transcription) == null ? void 0 : _j.language,
        translationLanguage: (_m = (_l = (_k = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _k.transcription) == null ? void 0 : _l.translation) == null ? void 0 : _m.language
      },
      options
    ),
    answerLabel: ((_n = rootData == null ? void 0 : rootData.answer) == null ? void 0 : _n.label) ?? (rootData == null ? void 0 : rootData.title)
  };
}
const languageMap = {
  af: "Afrikaans",
  sq: "Albanian",
  am: "Amharic",
  ar: "Arabic",
  hy: "Armenian",
  az: "Azerbaijani",
  eu: "Basque",
  be: "Belarusian",
  bn: "Bengali",
  bs: "Bosnian",
  bg: "Bulgarian",
  ca: "Catalan",
  zh: "Chinese (Simplified)",
  zh_TW: "Chinese (Traditional)",
  hr: "Croatian",
  cs: "Czech",
  da: "Danish",
  nl: "Dutch",
  en: "English",
  et: "Estonian",
  fi: "Finnish",
  fr: "French",
  fa: "Farsi",
  tl: "Filipino",
  ka: "Georgian",
  de: "German",
  el: "Greek",
  gu: "Gujarati",
  ht: "Haitian Creole",
  ha: "Hausa",
  he: "Hebrew",
  hi: "Hindi",
  hu: "Hungarian",
  is: "Icelandic",
  id: "Indonesian",
  ga: "Irish",
  it: "Italian",
  ja: "Japanese",
  kn: "Kannada",
  kk: "Kazakh",
  ko: "Korean",
  lv: "Latvian",
  lt: "Lithuanian",
  mk: "Macedonian",
  ms: "Malay",
  ml: "Malayalam",
  mt: "Maltese",
  mr: "Marathi",
  mn: "Mongolian",
  no: "Norwegian",
  ps: "Pashto",
  pl: "Polish",
  pt: "Portuguese",
  pa: "Punjabi",
  ro: "Romanian",
  ru: "Russian",
  sr: "Serbian",
  si: "Sinhala",
  sk: "Slovak",
  sl: "Slovenian",
  so: "Somali",
  es: "Spanish",
  sw: "Swahili",
  sv: "Swedish",
  ta: "Tamil",
  te: "Telugu",
  th: "Thai",
  tr: "Turkish",
  uk: "Ukrainian",
  ur: "Urdu",
  uz: "Uzbek",
  vi: "Vietnamese",
  cy: "Welsh"
};
function deriveLanguages({ data }) {
  var _a, _b;
  return ((_b = (_a = data == null ? void 0 : data.questions) == null ? void 0 : _a.items) == null ? void 0 : _b.reduce((acc, question) => {
    var _a2, _b2, _c, _d, _e, _f, _g;
    const originalLanguage = (_c = (_b2 = (_a2 = question == null ? void 0 : question.answer) == null ? void 0 : _a2.transcription) == null ? void 0 : _b2.language) == null ? void 0 : _c.split("-")[0];
    const translationLanguage = (_g = (_f = (_e = (_d = question == null ? void 0 : question.answer) == null ? void 0 : _d.transcription) == null ? void 0 : _e.translation) == null ? void 0 : _f.language) == null ? void 0 : _g.split(
      "-"
    )[0];
    if (originalLanguage && languageMap[originalLanguage] && !acc.find((language) => language.value === originalLanguage)) {
      acc.push({
        value: originalLanguage,
        label: languageMap[originalLanguage]
      });
    }
    if (translationLanguage && languageMap[translationLanguage] && !acc.find((language) => language.value === translationLanguage)) {
      acc.push({
        value: translationLanguage,
        label: languageMap[translationLanguage]
      });
    }
    return acc;
  }, [])) ?? [];
}
export {
  deriveData,
  deriveLanguages
};
//# sourceMappingURL=video-utils.js.map
